import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { IMG } from "@/lib/images";
import { fetchJournalEntries, sortOldestFirst, JOURNAL_FALLBACK } from "@/lib/journal";

export const FoundersJournal = () => {
  const [entries, setEntries] = useState(() => sortOldestFirst(JOURNAL_FALLBACK));

  useEffect(() => {
    let mounted = true;
    fetchJournalEntries().then((data) => {
      if (!mounted) return;
      if (Array.isArray(data) && data.length) setEntries(sortOldestFirst(data));
    });
    return () => { mounted = false; };
  }, []);

  return (
    <section id="journal" data-testid="journal-section" className="relative py-28 md:py-36 bg-[#0A0A0C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="section-mark" />
              <span className="overline">Founder&apos;s Journal · Behind The Build</span>
            </div>
            <h2 className="h-section text-white text-4xl sm:text-5xl lg:text-6xl">
              Notes from <br />
              <span className="text-[#F47A20]">the workshop floor.</span>
            </h2>
          </div>
          <p className="text-white/55 max-w-sm">
            Raw, unfiltered updates from the build. No marketing. No spin. Just what&apos;s happening.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative rounded-sm overflow-hidden border border-white/10 mb-12 bg-[#141417]"
          data-testid="journal-storyboard"
        >
          <img
            src={IMG.RIDE_COAST}
            alt="John riding the Road Glide 3 — Founder's Journal"
            loading="lazy"
            decoding="async"
            style={{ objectPosition: "50% 45%" }}
            className="w-full h-[280px] md:h-[420px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/30 to-transparent" />
          <div className="absolute top-4 left-4 tactical-badge text-[9px]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F47A20] animate-pulse" />
            The Build · All Entries
          </div>
          <div className="absolute bottom-5 left-5 right-5">
            <div className="h-display text-2xl md:text-4xl text-white leading-tight">From the workshop floor to the highway.</div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="journal-entry-grid">
          {entries.map((e, i) => (
            <motion.article
              key={e.id || e.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="group relative bg-[#141417] border border-white/5 rounded-sm overflow-hidden hover:border-[#F47A20]/40 transition-all"
              data-testid={`journal-entry-${i}`}
            >
              <div className="relative aspect-[5/4] overflow-hidden">
                <img
                  src={e.image_url}
                  alt={e.date_label}
                  style={{ objectPosition: e.object_pos || "50% 50%" }}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141417] via-transparent to-transparent" />
                <div className="absolute top-3 left-3 tactical-badge text-[9px]">{e.tag}</div>
              </div>
              <div className="p-6">
                <div className="serial text-[10px] text-[#F47A20] mb-2">{(e.date_label || "").toUpperCase()}</div>
                <h3 className="h-display text-xl text-white mb-3 leading-tight">{e.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{e.body}</p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a href="/journal" data-testid="journal-archive-link" className="btn-secondary">
            View Full Journal Archive
          </a>
        </div>
      </div>
    </section>
  );
};
